import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { getJobBranding, saveJobBranding } from "../services/jobService";
import "./RecruiterJobs.css";

const NAME_MAX = 80;
// Must stay under the backend's _BRANDING_LOGO_MAX_CHARS (250 000).
const LOGO_MAX_CHARS = 240_000;
const LOGO_MAX_W = 480;
const LOGO_MAX_H = 160;

// Downscale in the browser so only a small data URL is ever stored — a
// header logo never needs more than ~160px of height.
function resizeLogo(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the image file."));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("That file is not a valid image."));
      img.onload = () => {
        const scale = Math.min(1, LOGO_MAX_W / img.width, LOGO_MAX_H / img.height);
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        // PNG keeps transparency; fall back to lossy formats only if too big.
        const candidates = [
          canvas.toDataURL("image/png"),
          canvas.toDataURL("image/webp", 0.85),
          canvas.toDataURL("image/jpeg", 0.85),
        ];
        const fit = candidates.find((d) => d.length <= LOGO_MAX_CHARS && /^data:image\/(png|webp|jpeg);/.test(d));
        if (fit) resolve(fit);
        else reject(new Error("Logo is too large even after resizing — please use a simpler image."));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function JobBrandingModal({ open, jobId, jobTitle, onClose }) {
  const [name, setName] = useState("");
  const [logo, setLogo] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !jobId) return undefined;
    let active = true;
    setLoading(true); setError("");
    getJobBranding(jobId).then((b) => {
      if (!active) return;
      setName(b.name); setLogo(b.logo); setLoading(false);
    });
    return () => { active = false; };
  }, [open, jobId]);

  if (!open) return null;

  const handleFile = async (file) => {
    if (!file) return;
    setError("");
    try {
      setLogo(await resizeLogo(file));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      await saveJobBranding(jobId, { name: name.trim(), logo });
      toast.success(name.trim() || logo ? "Branding saved for this job." : "Branding removed — candidates will see Vagmee only.");
      onClose();
    } catch (err) {
      setError(err.message || "Unable to save branding");
    } finally {
      setSaving(false);
    }
  };

  const trimmed = name.trim();
  const hasBrand = !!(trimmed || logo);

  return (
    <div className="recruiter-modal-backdrop" role="presentation" onClick={onClose}>
      <div className="recruiter-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="recruiter-modal-header">
          <div>
            <h2>Candidate page branding</h2>
            <p>{jobTitle} — shown to candidates as “your company, powered by Vagmee”. Logo, name, or both.</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close">x</button>
        </div>

        <div className="job-form">
          {error && <div className="job-form-error">{error}</div>}
          {loading ? <p className="rd-muted">Loading…</p> : (
            <>
              <label>
                <span>Company name (optional)</span>
                <input value={name} maxLength={NAME_MAX} onChange={(e) => setName(e.target.value)} placeholder="e.g., Acme Corp" />
              </label>

              <div className="job-form-block">
                <span className="job-form-block-label">Company logo (optional) — PNG, JPG, WebP or SVG</span>
                <div className="branding-logo-row">
                  {logo && <img src={logo} alt="Company logo" className="branding-logo-thumb" />}
                  <label className="secondary-button branding-file-btn">
                    {logo ? "Replace logo" : "Upload logo"}
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" style={{ display: "none" }}
                      onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }} />
                  </label>
                  {logo && <button className="link-button" type="button" onClick={() => setLogo("")}>Remove logo</button>}
                </div>
              </div>

              <div className="job-form-block">
                <span className="job-form-block-label">Preview</span>
                <div className="branding-preview">
                  {hasBrand ? (
                    <>
                      <div className="cobrand-row">
                        {logo && <img src={logo} alt="" className="cobrand-logo" />}
                        {trimmed && <span className="cobrand-name">{trimmed}</span>}
                      </div>
                      <div className="cobrand-powered">
                        <span>powered by</span>
                        <img src="/Vagmee_Logo.png" alt="" className="cobrand-vagmee-logo" />
                        <strong>Vagmee</strong>
                        <span className="cobrand-sep">·</span>
                        <span>www.vagmee.in</span>
                      </div>
                    </>
                  ) : (
                    <div className="cobrand-row">
                      <img src="/Vagmee_Logo.png" alt="" className="cobrand-logo" />
                      <span className="cobrand-name">Vagmee</span>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="recruiter-modal-actions">
          <button className="secondary-button" type="button" onClick={onClose}>Cancel</button>
          <button className="primary-button" type="button" onClick={handleSave} disabled={saving || loading}>
            {saving ? "Saving…" : "Save branding"}
          </button>
        </div>
      </div>
    </div>
  );
}
